import React from 'react';
import { CheckCircle, Circle, AlertCircle, Globe, Image, Brain, Hash, Link, FileText, Shield, Clock } from 'lucide-react';

interface AnalysisStep {
  id: string;
  type: 'relevance' | 'ai_analysis' | 'enhanced_ai' | 'web_crawl' | 'image_ocr' | 'url_extraction' | 'entity_extraction' | 'threat_detection';
  name: string;
  status: 'completed' | 'in_progress' | 'pending' | 'failed';
  timestamp?: string;
  duration?: number;
  details: {
    model?: string;
    confidence?: number;
    score?: number;
    extractedData?: any;
    error?: string;
    metadata?: Record<string, any>;
  };
}

interface AnalysisStepsProps {
  postId: string;
  steps: AnalysisStep[];
}

export function AnalysisSteps({ postId, steps }: AnalysisStepsProps) {
  const getStepIcon = (type: AnalysisStep['type']) => {
    switch (type) {
      case 'relevance':
      case 'relevance_check':
        return <Hash className="w-5 h-5" />;
      case 'ai_analysis':
      case 'enhanced_ai':
      case 'threat_assessment':
        return <Brain className="w-5 h-5" />;
      case 'web_crawl':
        return <Globe className="w-5 h-5" />;
      case 'image_ocr':
        return <Image className="w-5 h-5" />;
      case 'url_extraction':
        return <Link className="w-5 h-5" />;
      case 'entity_extraction':
      case 'ioc_extraction':
        return <FileText className="w-5 h-5" />;
      case 'threat_detection':
      case 'alert_generation':
        return <Shield className="w-5 h-5" />;
      default:
        return <Circle className="w-5 h-5" />;
    }
  };

  const getStatusIcon = (status: AnalysisStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Circle className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-300" />;
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const renderExtractedData = (data: any, type: string) => {
    if (!data) return null;

    switch (type) {
      case 'relevance':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Relevance Score:</span>
                <span className="font-medium">{(data.score * 100).toFixed(1)}%</span>
              </div>
              {data.keywords && (
                <div className="mt-2">
                  <span className="text-gray-600">Keywords Found:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.keywords.map((keyword: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'ai_analysis':
      case 'enhanced_ai':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm space-y-2">
              {data.threat_level && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Threat Level:</span>
                  <span className={`font-medium px-2 py-1 rounded text-xs ${
                    data.threat_level === 'critical' ? 'bg-red-100 text-red-700' :
                    data.threat_level === 'high' ? 'bg-orange-100 text-orange-700' :
                    data.threat_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {data.threat_level.toUpperCase()}
                  </span>
                </div>
              )}
              {data.confidence && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Confidence:</span>
                  <span className="font-medium">{(data.confidence * 100).toFixed(1)}%</span>
                </div>
              )}
              {data.threat_types && data.threat_types.length > 0 && (
                <div>
                  <span className="text-gray-600">Threat Types:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.threat_types.map((type: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.attribution && (
                <div>
                  <span className="text-gray-600">Attribution:</span>
                  <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-medium">
                    {data.attribution}
                  </span>
                </div>
              )}
              {data.ttps && data.ttps.length > 0 && (
                <div>
                  <span className="text-gray-600">TTPs (MITRE ATT&CK):</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.ttps.map((ttp: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded font-mono">
                        {ttp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.iocs && data.iocs.length > 0 && (
                <div>
                  <span className="text-gray-600">{type === 'enhanced_ai' ? 'Advanced IOCs:' : 'IOCs Detected:'}</span>
                  <ul className="mt-1 space-y-1">
                    {data.iocs.slice(0, type === 'enhanced_ai' ? 10 : 5).map((ioc: any, idx: number) => (
                      <li key={idx} className="text-xs font-mono bg-gray-100 p-1 rounded">
                        {typeof ioc === 'string' ? ioc : (ioc.type ? `${ioc.type}: ${ioc.value}` : ioc)}
                      </li>
                    ))}
                    {data.iocs.length > (type === 'enhanced_ai' ? 10 : 5) && (
                      <li className="text-xs text-gray-500">... and {data.iocs.length - (type === 'enhanced_ai' ? 10 : 5)} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'web_crawl':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm space-y-2">
              {data.url && (
                <div>
                  <span className="text-gray-600">URL:</span>
                  <a href={data.url} target="_blank" rel="noopener noreferrer" 
                     className="text-blue-500 hover:underline text-xs block truncate">
                    {data.url}
                  </a>
                </div>
              )}
              {data.title && (
                <div>
                  <span className="text-gray-600">Title:</span>
                  <p className="text-xs font-medium">{data.title}</p>
                </div>
              )}
              {data.threat_score !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Threat Score:</span>
                  <span className="font-medium">{data.threat_score}/100</span>
                </div>
              )}
              {data.suspicious_patterns && data.suspicious_patterns.length > 0 && (
                <div>
                  <span className="text-gray-600">Suspicious Patterns:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.suspicious_patterns.map((pattern: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                        {pattern}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'image_ocr':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm space-y-2">
              {data.image_count !== undefined && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Images Processed:</span>
                  <span className="font-medium">{data.image_count}</span>
                </div>
              )}
              {data.text_extracted && (
                <div>
                  <span className="text-gray-600">Extracted Text:</span>
                  <p className="text-xs mt-1 p-2 bg-white rounded border">{data.text_extracted}</p>
                </div>
              )}
              {data.threats_detected && data.threats_detected.length > 0 && (
                <div>
                  <span className="text-gray-600">Threats in Images:</span>
                  <ul className="mt-1 space-y-1">
                    {data.threats_detected.map((threat: string, idx: number) => (
                      <li key={idx} className="text-xs text-red-600">• {threat}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'url_extraction':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">URLs Found:</span>
                <span className="font-medium">{data.count || 0}</span>
              </div>
              {data.urls && data.urls.length > 0 && (
                <div>
                  <span className="text-gray-600">Extracted URLs:</span>
                  <ul className="mt-1 space-y-1">
                    {data.urls.slice(0, 3).map((url: string, idx: number) => (
                      <li key={idx} className="text-xs font-mono bg-gray-100 p-1 rounded truncate">
                        {url}
                      </li>
                    ))}
                    {data.urls.length > 3 && (
                      <li className="text-xs text-gray-500">... and {data.urls.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'entity_extraction':
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <div className="text-sm space-y-2">
              {data.ips && data.ips.length > 0 && (
                <div>
                  <span className="text-gray-600">IP Addresses:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.ips.map((ip: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded font-mono">
                        {ip}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.domains && data.domains.length > 0 && (
                <div>
                  <span className="text-gray-600">Domains:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.domains.map((domain: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {domain}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {data.hashes && data.hashes.length > 0 && (
                <div>
                  <span className="text-gray-600">File Hashes:</span>
                  <ul className="mt-1 space-y-1">
                    {data.hashes.slice(0, 3).map((hash: string, idx: number) => (
                      <li key={idx} className="text-xs font-mono bg-gray-100 p-1 rounded truncate">
                        {hash}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <pre className="text-xs overflow-auto">{JSON.stringify(data, null, 2)}</pre>
          </div>
        );
    }
  };

  const formatProviderName = (provider?: string) => {
    if (!provider) return '';
    const providerNames: Record<string, string> = {
      'cloudflare': 'Cloudflare AI',
      'anthropic': 'Anthropic',
      'openai': 'OpenAI',
      'grok': 'Grok',
      'gemini': 'Google Gemini',
      'mistral': 'Mistral AI',
      'deepseek': 'DeepSeek'
    };
    return providerNames[provider] || provider;
  };

  const formatModelName = (model?: string) => {
    if (!model) return '';
    
    // Special case for rule engine
    if (model === 'rule-engine') return 'Rule Engine';
    
    // Return the exact model name as configured in settings
    // This ensures users see exactly what model was used for each step
    return model;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          AI Analysis Steps
        </h3>
        <div className="text-sm text-gray-500">
          {steps.filter(s => s.status === 'completed').length} of {steps.length} completed
        </div>
      </div>

      <div className="space-y-3">
        {steps.map((step) => (
          <div key={step.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-0.5">{getStepIcon(step.type)}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{step.name}</h4>
                    {getStatusIcon(step.status)}
                  </div>
                  
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                    {step.timestamp && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(step.timestamp).toLocaleTimeString()}
                      </div>
                    )}
                    {step.duration !== undefined && (
                      <div>Duration: {formatDuration(step.duration)}</div>
                    )}
                    {(step.details?.model || step.details?.metadata?.provider) && (
                      <div className="flex items-center gap-1">
                        <Brain className="w-3 h-3" />
                        <span className="text-xs font-medium">
                          {step.details?.model ? formatModelName(step.details.model) : 
                           (step.details?.metadata?.provider && formatProviderName(step.details.metadata.provider))}
                        </span>
                      </div>
                    )}
                  </div>

                  {step.status === 'failed' && step.details?.error && (
                    <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded">
                      Error: {step.details.error}
                    </div>
                  )}

                  {step.status === 'completed' && step.details?.extractedData && renderExtractedData(step.details.extractedData, step.type)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {steps.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No analysis steps available for this post
        </div>
      )}
    </div>
  );
}